import { _, helpers, mui, React, ts, ui } from '_core';

import Tabular from '../../../tables/tabular';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  title: string;
};

const Chart = ({ id, readOnly, widgetKey, loadWidgetData, file, fullScreen }: ChartProps) => {
  const [rSquaredData, setRSquaredData] = React.useState<ts.types.widgets.TableData>(null);
  const [parameterSeriesData, setParameterSeriesData] = React.useState<Record<string, ts.types.widgets.TableData>>({});
  const [parameterTableData, setParameterTableData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<string>('rSquared');
  const [selectedParameterTab, setSelectedParameterTab] = React.useState<string>('all');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: { file },
      });
      if (response) {
        const newRSquaredData = _.map(response.data, (row) => ({
          date: row.build_date,
          'In Sample R-Squared': row.in_sample_r2,
          'Out of Sample R-Squared': row.out_of_sample_r2,
        }));

        // Create one series for each parameter in a record mapping parameter name to the series.
        const parameters = _.difference(Object.keys(response.data[0]), [
          'build_date',
          'model_date',
          'in_sample_r2',
          'out_of_sample_r2',
          'metric',
        ]);
        const newParameterSeriesData = _.reduce(
          parameters,
          (acc, param) => ({
            ...acc,
            [param]: _.map(response.data, (row) => ({
              date: row.build_date,
              [param]: row[param],
            })),
          }),
          {}
        );
        const newParameterTableData = _.uniqBy(
          _.map(response.data, (row) => ({
            date: row.model_date,
            ..._.pick(row, parameters),
          })),
          'date'
        );
        setRSquaredData(newRSquaredData);
        setParameterSeriesData(newParameterSeriesData);
        setParameterTableData(newParameterTableData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => {
      setRSquaredData(null);
    };
  }, [file]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }} key={widgetKey}>
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(_e, newTab) => setSelectedTab(newTab)}
      >
        <mui.core.Tab label="R-Squared" value="rSquared" />
        <mui.core.Tab label="Parameters" value="parameters" />
      </mui.core.Tabs>
      {selectedTab === 'rSquared' && (
        <ui.LineChart
          id={id}
          data={rSquaredData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={'R-Squared'}
          numberFormat="#.###"
        />
      )}
      {selectedTab === 'parameters' && (
        <mui.core.Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedParameterTab}
          onChange={(_e, newTab) => setSelectedParameterTab(newTab)}
        >
          <mui.core.Tab label="All" value="all" />
          {Object.keys(parameterSeriesData).map((param) => (
            <mui.core.Tab key={param} label={param.replaceAll('_', ' ')} value={param} />
          ))}
        </mui.core.Tabs>
      )}
      {selectedTab === 'parameters' && selectedParameterTab == 'all' && (
        <Tabular<'all'>
          data={
            parameterTableData
              ? {
                  all: parameterTableData,
                }
              : null
          }
          getColumns={{
            all: () => [
              { name: 'Date', key: 'date' },

              ...Object.keys(parameterSeriesData).map((param) => ({
                name: param.replaceAll('_', ' '),
                key: param,
                filter: 'number',
              })),
            ],
          }}
          sheetTitles={{ all: 'All' }}
          fullScreen={true}
          currentSheet={'all'}
        ></Tabular>
      )}
      {selectedTab === 'parameters' && selectedParameterTab != 'all' && (
        <ui.LineChart
          key={selectedParameterTab}
          id={id}
          data={parameterSeriesData[selectedParameterTab]}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={`Parameter - ${selectedParameterTab}`}
        />
      )}
    </mui.core.Box>
  );
};

export default Chart;
